'use client';

import AppImage from '@/components/ui/AppImage';

interface ProjectCardProps {
  project: {
    id: string;
    slug: string;
    title: string;
    titleDe: string;
    date: string;
    author: string;
    category: string;
    categoryDe: string;
    summary: string;
    summaryDe: string;
    coverImage: string;
    tags: string[];
    tagsDe: string[];
    ctaLabel: string;
    ctaLabelDe: string;
  };
  currentLanguage: 'en' | 'de';
  onViewDetails: () => void;
  index: number;
  translations: {
    viewCase: string;
  };
}

const ProjectCard = ({ project, currentLanguage, onViewDetails, index, translations }: ProjectCardProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return currentLanguage === 'en' ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
      : date.toLocaleDateString('de-DE', { year: 'numeric', month: 'long' });
  };

  const isPlaceholder = project.ctaLabel === 'Still Classified' || project.ctaLabelDe === 'Still Classified';

  return (
    <article
      className={`group relative bg-secondary rounded-xl overflow-hidden shadow-card hover:shadow-cosmic transition-all duration-slow transform hover:-translate-y-2 ${isPlaceholder ? 'cursor-default' : ''} animate-fade-in`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Cover Image */}
      <div className="relative h-64 overflow-hidden">
        <AppImage
          src={project.coverImage}
          alt={currentLanguage === 'de' && project.titleDe ? project.titleDe : project.title}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          loading="lazy"
          className="object-cover transition-transform duration-slow group-hover:scale-110"
        />

        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="inline-block px-3 py-1 bg-accent/90 backdrop-blur-sm text-primary rounded-full text-xs font-semibold uppercase tracking-wider">
            {currentLanguage === 'en' ? project.category : project.categoryDe}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Metadata */}
        <div className="flex items-center justify-between text-xs text-foreground-muted">
          <span>{formatDate(project.date)}</span>
        </div>

        {/* Title */}
        <h2 className="font-heading font-bold text-xl text-foreground-light group-hover:text-accent transition-colors duration-fast line-clamp-2">
          {currentLanguage === 'de' && project.titleDe ? project.titleDe : project.title}
        </h2>

        {/* Author */}
        <p className="text-sm text-foreground-secondary">
          {currentLanguage === 'en' ? 'by' : 'von'} {project.author}
        </p>

        {/* Summary */}
        <p className="text-foreground-muted font-inter text-sm line-clamp-3">
          {currentLanguage === 'de' && project.summaryDe ? project.summaryDe : project.summary}
        </p>

        {/* Tags */}
        {project.tags && project.tags.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {(currentLanguage === 'de' && project.tagsDe ? project.tagsDe : project.tags)
              .slice(1, 4)
              .map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 bg-highlight/10 text-highlight rounded text-xs font-inter font-medium"
                >
                  {tag}
                </span>
              ))}
            {(currentLanguage === 'de' && project.tagsDe ? project.tagsDe.length : project.tags.length) > 4 && (
              <span className="px-2 py-1 bg-highlight/10 text-highlight rounded text-xs font-inter font-medium">
                +{(currentLanguage === 'de' && project.tagsDe ? project.tagsDe.length : project.tags.length) - 4}
              </span>
            )}
          </div>
        )}

        {/* CTA Button */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            if (!isPlaceholder) {
              onViewDetails();
            }
          }}
          className={`w-full mt-4 px-6 py-3 bg-primary border-2 border-accent text-accent rounded-md font-inter font-semibold text-sm transition-all duration-fast ${isPlaceholder ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent/10 hover:shadow-glow-cyan cursor-pointer'} group/btn`}
        >
          <span className="flex items-center justify-center">
            {currentLanguage === 'de' && project.ctaLabelDe ? project.ctaLabelDe : project.ctaLabel}
            {!isPlaceholder && (
              <svg
                className="w-4 h-4 ml-2 transition-transform duration-fast group-hover/btn:translate-x-1"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M9 5l7 7-7 7" />
              </svg>
            )}
          </span>
        </button>
      </div>

      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 border-2 border-accent opacity-0 group-hover:opacity-100 transition-opacity duration-fast rounded-xl pointer-events-none" />
    </article>
  );
};

export default ProjectCard;